import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { backendApiAddress } from "../backendApi";
import { RootState } from "./store";
import { Pattern } from "./pattern";
import { APIYarn } from "../services/yarnApi";

const adminFetchAllYarn = createAsyncThunk(
  "admin/fetchAllYarn",
  async (_arg, _thunkAPI) => {
    const response = await fetch(`${backendApiAddress}/api/yarn/all`, {
      credentials: "include",
    });
    return response.json();
  }
);

const adminSaveChangedYarn = createAsyncThunk(
  "admin/saveChangedYarn",
  async (_arg, thunkAPI) => {
    const {
      yarn: { remote, localChanges },
    } = (thunkAPI.getState() as RootState).admin;

    let responses = [];
    for (let { sku, hex, yarnId, yarnSku } of Object.values(localChanges)) {
      // "If hex has been changed locally"
      if (
        hex !== remote[yarnSku].colors.find((color) => sku === color.sku)?.hex
      ) {
        const request = fetch(`${backendApiAddress}/api/yarn/color/set_hex`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sku, hex, yarn_id: yarnId }),
        });
        responses.push(request);
      }
    }
    return Promise.allSettled(responses);
  }
);

const adminGenerateColorHex = createAsyncThunk(
  "admin/generateColorHex",
  async (sku: string) => {
    const response = await fetch(
      `${backendApiAddress}/api/yarn/color/${sku}/generate_hex`,
      { method: "POST", credentials: "include" }
    );
    const data = await response.json();
    return { sku, hex: data.hex as string | null };
  }
);

const adminGenerateMissingHexes = createAsyncThunk(
  "admin/generateMissingHexes",
  async () => {
    const response = await fetch(
      `${backendApiAddress}/api/yarn/color/generate_hex_all`,
      { method: "POST", credentials: "include" }
    );
    return response.json() as Promise<{
      processed: number;
      succeeded: number;
      failed: number;
    }>;
  }
);

const adminSetMulticolor = createAsyncThunk(
  "admin/setMulticolor",
  async ({ sku, isMulticolor }: { sku: string; isMulticolor: boolean }) => {
    await fetch(`${backendApiAddress}/api/yarn/color/${sku}/multicolor`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isMulticolor }),
    });
    return { sku, isMulticolor };
  }
);

const adminFetchAllPatterns = createAsyncThunk(
  "admin/fetchAllPatterns",
  async (_arg, _thunkAPI) => {
    const response = await fetch(`${backendApiAddress}/api/pattern`, {
      credentials: "include",
    });
    return response.json();
  }
);

const adminSavePattern = createAsyncThunk(
  "admin/savePattern",
  async (args: { patternId: number; pattern: Pattern }, thunkAPI) => {
    const { patternId, pattern } = args;

    if (pattern === undefined) {
      return new Promise((_, reject) =>
        reject(`Pattern ${patternId} has not been changed`)
      );
    }

    const response = await fetch(
      `${backendApiAddress}/api/pattern/${patternId}`,
      {
        method: "PUT",
        body: JSON.stringify(pattern),
        credentials: "include",
      }
    );
    return response.json();
  }
);

interface AdminType {
  yarn: YarnAdminType;
  pattern: PatternAdminType;
}

interface YarnAdminType {
  open: boolean;
  remote: { [key: string]: APIYarn };
  localChanges: { [key: string]: APIYarnColorChange };
}

interface APIYarnColorChange {
  sku: string;
  hex: string | null;

  yarnId: number;
  yarnSku: string;
}

interface PatternAdminType {
  open: boolean;
  remote: { [key: number]: Pattern };
  localChanges: { [key: number]: string };
}

const initialState: AdminType = {
  yarn: {
    open: false,
    remote: {},
    localChanges: {},
  },
  pattern: {
    open: false,
    remote: {},
    localChanges: {},
  },
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    toggleYarnOpen(state) {
      state.yarn.open = !state.yarn.open;
    },
    clearYarn(state) {
      state.yarn.remote = {};
      state.yarn.localChanges = {};
    },
    clearYarnChanges(state) {
      state.yarn.localChanges = {};
    },
    changeYarnColor(state, action) {
      const { sku, hex, yarnId, yarnSku } = action.payload;
      state.yarn.localChanges[sku] = { sku, hex, yarnId, yarnSku };
    },
    togglePatternOpen(state) {
      state.pattern.open = !state.pattern.open;
    },
    clearPattern(state) {
      state.pattern.remote = {};
      state.pattern.localChanges = {};
    },
    setPatternLocalChanges(state, action) {
      const { id, change }: { id: number; change: string } = action.payload;

      state.pattern.localChanges = {
        ...state.pattern.localChanges,
        [id]: change,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(adminFetchAllYarn.fulfilled, (state, action) => {
      const yarnMap: { [key: string]: APIYarn } = action.payload;
      state.yarn.remote = yarnMap;
    });
    builder.addCase(adminSaveChangedYarn.fulfilled, (state, action) => {
      // Reset yarn state, triggering new fetch from API
      state.yarn = {
        ...initialState.yarn,
        open: state.yarn.open,
      };
    });
    builder.addCase(adminGenerateColorHex.fulfilled, (state, action) => {
      const { sku, hex } = action.payload;
      for (const yarn of Object.values(state.yarn.remote)) {
        const color = yarn.colors.find((c) => c.sku === sku);
        if (color) {
          color.hex = hex;
          color.hex_source = "generated";
          break;
        }
      }
    });
    builder.addCase(adminGenerateMissingHexes.fulfilled, (state) => {
      state.yarn = { ...initialState.yarn, open: state.yarn.open };
    });
    builder.addCase(adminSetMulticolor.fulfilled, (state, action) => {
      const { sku, isMulticolor } = action.payload;
      for (const yarn of Object.values(state.yarn.remote)) {
        const color = yarn.colors.find((c) => c.sku === sku);
        if (color) {
          color.is_multicolor = isMulticolor;
          break;
        }
      }
    });
    builder.addCase(adminFetchAllPatterns.fulfilled, (state, action) => {
      const patterns: Pattern[] = action.payload;

      state.pattern.remote = Object.fromEntries(
        patterns.map((pattern) => [pattern.id, pattern])
      );
    });
  },
});

export {
  adminSlice,
  adminFetchAllYarn,
  adminSaveChangedYarn,
  adminGenerateColorHex,
  adminGenerateMissingHexes,
  adminSetMulticolor,
  adminFetchAllPatterns,
  adminSavePattern,
};

export const {
  toggleYarnOpen,
  clearYarn,
  clearYarnChanges,
  changeYarnColor,
  togglePatternOpen,
  clearPattern,
  setPatternLocalChanges,
} = adminSlice.actions;
